import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { Children, isValidElement, type ReactNode, useContext } from 'react';
import { ProductTableContext } from '../../../contexts/product-table-context';
import { activeFilterCount, resetFilters } from '../../../hooks/filter-fields';
import type { Filters } from '../../../hooks/products';
import { ToolbarPopover } from '../../toolbar/popover';
import type { FilterComponent } from './filter-component';

function isFilterComponent(type: unknown): type is FilterComponent {
  return typeof type === 'function' && 'field' in type;
}

/**
 * Wraps a page's filter children in the shared "Filters" popover, deriving the
 * active-filter badge count and "Clear all" target from those children — each
 * one declares the `Filters` field it controls (see `withFilterField`), so the
 * field set is read off the rendered controls rather than kept in a parallel
 * list that can silently drift out of sync.
 */
export function FiltersPopover({
  defaultFilters,
  children,
}: {
  defaultFilters: Filters;
  children: ReactNode;
}) {
  const { filters, setFilters } = useContext(ProductTableContext);

  const fields = Children.toArray(children)
    .filter(isValidElement)
    .map((child) => child.type)
    .filter(isFilterComponent)
    .map((type) => type.field);

  const onClear = () => {
    setFilters((current) => resetFilters(current, defaultFilters, fields));
  };

  return (
    <ToolbarPopover
      icon={<AdjustmentsHorizontalIcon className="size-5" />}
      label="Filters"
      badge={activeFilterCount(filters, defaultFilters, fields)}
      onClear={onClear}
    >
      {children}
    </ToolbarPopover>
  );
}
